import BaseService from './BaseService';
import apiClient from '../plugins/axios';

const BASE = '/livekit/sip/outbound-campaigns';

/** Background refreshes skip the global loading overlay and error toasts. */
const silentConfig = (options) => (options.silent ? { skipLoading: true, skipErrorNotification: true } : {});

/**
 * Outbound call campaigns: a list of phone numbers an agent calls periodically
 * through a SIP outbound trunk (MS-476).
 */
class OutboundCampaignService extends BaseService {
  constructor() {
    super(BASE);
  }

  /**
   * List campaigns (paginated, newest first).
   * @param {Object} [params] - { page, page_size, status }
   * @param {Object} [options] - { silent: true } for background refreshes
   * @returns {Promise<{items: Array, pagination: Object|null}>}
   */
  async list(params = {}, options = {}) {
    try {
      const response = await apiClient.get(BASE, { params, ...silentConfig(options) });
      return {
        items: response.data?.data || [],
        pagination: response.data?.pagination || null,
      };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Create a campaign. Body: { name, agent_id, sip_trunk_id, start_at?, end_at?, interval_seconds?,
   * batch_size?, max_attempts?, retry_delay_seconds?, start?, numbers? }
   * @returns {Promise<{campaign: Object, numbers: Object}>}
   */
  async create(body) {
    try {
      const response = await apiClient.post(BASE, body);
      return response.data?.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async get(id, options = {}) {
    try {
      const response = await apiClient.get(`${BASE}/${encodeURIComponent(id)}`, silentConfig(options));
      return response.data?.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /** PATCH campaign; omitted fields are unchanged. */
  async patch(id, body) {
    try {
      const response = await apiClient.patch(`${BASE}/${encodeURIComponent(id)}`, body);
      return response.data?.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async remove(id) {
    try {
      const response = await apiClient.delete(`${BASE}/${encodeURIComponent(id)}`);
      return response.data?.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Run a status action on a campaign.
   * @param {string} id
   * @param {'start'|'pause'|'cancel'} action
   */
  async action(id, action) {
    try {
      const response = await apiClient.post(`${BASE}/${encodeURIComponent(id)}/${action}`);
      return response.data?.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Add numbers as JSON. numbers: [{ phone_number, name?, metadata? }]
   * @returns {Promise<{received, added, duplicates, rejected}>}
   */
  async addNumbers(id, numbers) {
    try {
      const response = await apiClient.post(`${BASE}/${encodeURIComponent(id)}/numbers`, { numbers });
      return response.data?.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Upload a CSV file of numbers (multipart field "file", max 5 MB).
   * @returns {Promise<{received, added, duplicates, rejected}>}
   */
  async uploadNumbersCsv(id, file) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const url = `${BASE}/${encodeURIComponent(id)}/numbers/csv`;
      const response = await apiClient.post(url, formData, { timeout: 60000 });
      return response.data?.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * List a campaign's numbers (paginated, insertion order).
   * @param {Object} [params] - { page, page_size, status }
   * @param {Object} [options] - { silent: true } for background refreshes
   */
  async listNumbers(id, params = {}, options = {}) {
    try {
      const response = await apiClient.get(`${BASE}/${encodeURIComponent(id)}/numbers`, {
        params,
        ...silentConfig(options),
      });
      return {
        items: response.data?.data || [],
        pagination: response.data?.pagination || null,
      };
    } catch (error) {
      throw this.handleError(error);
    }
  }
}

export default new OutboundCampaignService();
